import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { BlockUI } from 'primereact/blockui';
import PageHeader, { tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj } from '../common/TableSearch';
import { ocitavanjaApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { fmtDate } from '../../utils/dates';
import { exportXlsx, obojiRed, stampaVremena } from '../../utils/excel';
import { ROK_KARTICA, ROK_VOZILO, PRAG_PRIKAZA, stanje, vaznostKartice, poHitnosti } from './rokovi';
import { generisiOpomene } from './opomenaWord';
import ImportResultDialog from '../TachoReader/ImportResultDialog';

const FILTERI = [
    { label: 'Sve', value: 'ALL' },
    { label: `Za očitavanje (do ${PRAG_PRIKAZA} dana do roka)`, value: 'ACTION' },
    { label: 'Istekao rok ili nikad očitano', value: 'EXPIRED' },
];
const BEZ_FIRME = 'Bez kompanije';
/* grupisanje po firmi (tabela i opomena), unutar firme po hitnosti; 'Bez kompanije' na kraju */
const poFirmi = (a, b) => ((a.firma === BEZ_FIRME) - (b.firma === BEZ_FIRME)) || a.firma.localeCompare(b.firma, 'sr') || poHitnosti(a, b);

/** Pregled poslednjih očitavanja po firmi + izvoz u Excel i Word opomena ovlašćenom licu. */
export default function Ocitavanja({ active }) {
    const alerts = useAlerts();
    const companies = useSelector((s) => s.companies.list);
    const [companyIds, setCompanyIds] = useState([]);
    const [samoAktivni, setSamoAktivni] = useState(true);
    const [filter, setFilter] = useState('ACTION');
    const [pretraga, setPretraga] = useState('');
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState(0);
    const [uvoz, setUvoz] = useState(null); // fajlovi koji se upravo uvoze
    const fileRef = useRef(null);

    const ucitaj = async () => {
        setBusy(true);
        try { setData(await ocitavanjaApi.overview({ companyIds, activeOnly: samoAktivni })); } catch { /* toast */ }
        setBusy(false);
    };
    useEffect(() => { if (active && !data) ucitaj(); }, [active]);

    const firmaPoId = useMemo(() => new Map((data ? data.companies : []).map((c) => [c.id, c])), [data]);
    const nazivFirme = (id) => (id == null ? BEZ_FIRME : (firmaPoId.get(id) || {}).name || BEZ_FIRME);

    const vozaci = useMemo(() => (data ? data.drivers : []).map((d) => ({
        ...d, naziv: d.name, firma: nazivFirme(d.companyId), last: d.lastDownload, approx: d.lastActivity,
        st: stanje({ last: d.lastDownload, approx: d.lastActivity, rok: ROK_KARTICA, noCard: !d.cardNumber }),
        vaznost: vaznostKartice(d.cardExpiry),
    })).sort(poFirmi), [data, firmaPoId]);
    const vozila = useMemo(() => (data ? data.vehicles : []).map((v) => ({
        ...v, naziv: v.licenceNumber, firma: nazivFirme(v.companyId), last: v.lastDownload, approx: null,
        st: stanje({ last: v.lastDownload, rok: ROK_VOZILO }),
    })).sort(poFirmi), [data, firmaPoId]);

    const prolazi = (r) => {
        if (filter === 'ACTION' && !r.st.needsAction) return false;
        if (filter === 'EXPIRED' && !(r.st.kind === 'EXPIRED' || r.st.kind === 'NEVER')) return false;
        return true;
    };
    /* pretraga po svim kolonama (zajednička za obe tabele) */
    const vrednosti = (r) => [r.naziv, r.firma, r.cardNumber, r.identificationNumber, fmtDate(r.cardExpiry), fmtDate(r.last || r.approx), r.st.daysSince, r.st.label];
    const vozaciFilter = vozaci.filter(prolazi);
    const vozilaFilter = vozila.filter(prolazi);
    const vozaciPrikaz = filtriraj(vozaciFilter, pretraga, vrednosti);
    const vozilaPrikaz = filtriraj(vozilaFilter, pretraga, vrednosti);

    const brojac = (lista) => ({
        istekao: lista.filter((r) => r.st.kind === 'EXPIRED' || r.st.kind === 'NEVER').length,
        upozorenje: lista.filter((r) => r.st.kind === 'WARN').length,
        uskoro: lista.filter((r) => r.st.kind === 'SOON').length,
        uRedu: lista.filter((r) => r.st.kind === 'OK').length,
    });
    const bk = brojac(vozaci);
    const bv = brojac(vozila);

    /* ── izvoz ── */
    const kolone = (vrsta) => [
        { header: 'Firma', key: 'firma' },
        ...(vrsta === 'k'
            ? [{ header: 'Vozač', key: 'naziv' }, { header: 'Kartica', value: (r) => r.cardNumber || '' },
                { header: 'Kartica važi do', value: (r) => (r.cardExpiry ? fmtDate(r.cardExpiry) : '') }]
            : [{ header: 'Registracija', key: 'naziv' }, { header: 'Broj šasije', value: (r) => r.identificationNumber || '' }]),
        { header: 'Poslednje očitavanje', value: (r) => (r.last ? fmtDate(r.last) : r.approx ? `${fmtDate(r.approx)} (poslednja aktivnost)` : '') },
        { header: 'Dana od očitavanja', value: (r) => (r.st.daysSince ?? '') },
        { header: 'Rok (dana)', value: () => (vrsta === 'k' ? ROK_KARTICA : ROK_VOZILO) },
        { header: 'Stanje', value: (r) => r.st.label },
    ];
    const boja = (xr, r) => {
        if (r.st.kind === 'EXPIRED' || r.st.kind === 'NEVER') obojiRed(xr, 'FFFDECED');
        else if (r.st.kind === 'WARN') obojiRed(xr, 'FFFDF3E1');
    };
    const izvozExcel = () => exportXlsx({
        fileName: `Poslednja ocitavanja - ${stampaVremena()}.xlsx`,
        sheets: [
            { sheetName: 'Kartice vozača', title: `Poslednja očitavanja kartica vozača (rok ${ROK_KARTICA} dana)`, subtitle: `Stanje na dan ${fmtDate(new Date())} · ${FILTERI.find((f) => f.value === filter).label}`, columns: kolone('k'), rows: vozaciPrikaz, rowStyle: boja },
            { sheetName: 'Tahografi vozila', title: `Poslednja očitavanja tahografa vozila (rok ${ROK_VOZILO} dana)`, subtitle: `Stanje na dan ${fmtDate(new Date())} · ${FILTERI.find((f) => f.value === filter).label}`, columns: kolone('v'), rows: vozilaPrikaz, rowStyle: boja },
        ],
    }).catch(() => alerts.error('Izvoz u Excel nije uspeo'));

    /* Opomena: firme (sa podacima) koje imaju bar jedan vozač/vozilo za očitavanje u trenutnom prikazu */
    const zaOpomenu = useMemo(() => {
        const mapa = new Map();
        const dodaj = (r, gde) => {
            if (!r.st.needsAction || r.companyId == null) return;
            if (!mapa.has(r.companyId)) mapa.set(r.companyId, { firma: firmaPoId.get(r.companyId), vozaci: [], vozila: [] });
            mapa.get(r.companyId)[gde].push(r);
        };
        vozaciPrikaz.forEach((r) => dodaj(r, 'vozaci'));
        vozilaPrikaz.forEach((r) => dodaj(r, 'vozila'));
        return [...mapa.values()].filter((f) => f.firma).sort((a, b) => a.firma.name.localeCompare(b.firma.name, 'sr'));
    }, [vozaciPrikaz, vozilaPrikaz, firmaPoId]);
    const bezOvlascenog = zaOpomenu.filter((f) => !f.firma.mainPerson).length;

    const word = async () => {
        try {
            await generisiOpomene(zaOpomenu);
            alerts.success(`Opomena je napravljena za ${zaOpomenu.length} ${zaOpomenu.length === 1 ? 'firmu' : 'firmi'}`);
        } catch { alerts.error('Pravljenje Word dokumenta nije uspelo'); }
    };

    /* ── prikaz ── */
    const stanjeBody = (r) => (
        <span className={`pill ${r.st.severity}`} title={r.st.basis === 'aktivnost' ? 'Procena prema poslednjem danu sa aktivnostima (uvoz pre evidencije očitavanja)' : undefined}>
            {r.st.label}
        </span>
    );
    const poslednjeBody = (r) => (r.last ? fmtDate(r.last) : r.approx
        ? <span title="Procena prema poslednjem danu sa aktivnostima">{fmtDate(r.approx)}<span className="muted"> *</span></span>
        : <span className="muted">—</span>);
    const grupa = (r) => <span className="group-head"><i className="pi pi-building" aria-hidden="true" /> {r.firma}</span>;
    const redKlasa = (r) => (r.st.kind === 'EXPIRED' || r.st.kind === 'NEVER' ? 'row-danger' : r.st.kind === 'WARN' ? 'row-warn' : '');

    return (
        <BlockUI blocked={busy} template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem' }} aria-label="Učitavanje" />}>
            <input ref={fileRef} type="file" accept=".ddd,.esm" multiple hidden
                onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ''; if (f.length) setUvoz(f); }} />
            <PageHeader title="Očitavanja" subtitle={`Rokovi za preuzimanje podataka: kartica vozača najkasnije na ${ROK_KARTICA} dana, tahograf vozila najkasnije na ${ROK_VOZILO} dana.`}>
                <Button label="Učitaj .DDD fajlove" icon="pi pi-upload" outlined tooltip="Kartice vozača i tahografi vozila — beleži se datum očitavanja"
                    tooltipOptions={{ position: 'bottom' }} onClick={() => fileRef.current.click()} />
                <Button label="Excel" icon="pi pi-file-excel" outlined severity="secondary" onClick={izvozExcel} disabled={!data || (!vozaciPrikaz.length && !vozilaPrikaz.length)} />
                <Button label="Word opomena" icon="pi pi-file-word" onClick={word} disabled={!zaOpomenu.length}
                    tooltip={zaOpomenu.length ? `Opomena za ${zaOpomenu.length} ${zaOpomenu.length === 1 ? 'firmu' : 'firmi'} — samo vozači i vozila kojima ističe ili je istekao rok` : 'Nema firmi kojima ističe rok u trenutnom prikazu'}
                    tooltipOptions={{ position: 'bottom', showOnDisabled: true }} />
            </PageHeader>

            <div className="panel">
                <div className="filters">
                    <div className="field" style={{ flexBasis: '17rem' }}>
                        <label htmlFor="oc-co">Kompanija</label>
                        <MultiSelect inputId="oc-co" value={companyIds} options={companies} optionLabel="name" optionValue="id" filter
                            placeholder="Sve kompanije" maxSelectedLabels={1} selectedItemsLabel="{0} izabrano" onChange={(e) => setCompanyIds(e.value)} />
                    </div>
                    <div className="field" style={{ flexBasis: '20rem' }}>
                        <label htmlFor="oc-st">Prikaz</label>
                        <Dropdown inputId="oc-st" value={filter} options={FILTERI} onChange={(e) => setFilter(e.value)} />
                    </div>
                    <label className="check inline-field" htmlFor="oc-akt">
                        <Checkbox inputId="oc-akt" checked={samoAktivni} onChange={(e) => setSamoAktivni(e.checked)} /> Samo aktivni vozači
                    </label>
                    <div className="filters-actions">
                        <Button label="Prikaži" icon="pi pi-refresh" severity="secondary" outlined onClick={ucitaj} disabled={busy} />
                    </div>
                </div>
                {bezOvlascenog > 0 && (
                    <p className="panel-note"><i className="pi pi-exclamation-circle" aria-hidden="true" />
                        {bezOvlascenog} {bezOvlascenog === 1 ? 'firma nema' : 'firme nemaju'} upisano odgovorno lice — opomena će biti bez imena primaoca (Kompanije → Odgovorno lice).</p>
                )}
            </div>

            <div className="stat-row">
                <div className="stat"><span className="stat-label">Kartice — istekao rok / nikad</span><span className="stat-value text-danger">{bk.istekao}</span></div>
                <div className="stat"><span className="stat-label">Kartice — ističe za ≤ 7 dana</span><span className="stat-value text-warn">{bk.upozorenje}</span></div>
                <div className="stat"><span className="stat-label">Kartice — ističe za ≤ 15 dana</span><span className="stat-value">{bk.uskoro}</span></div>
                <div className="stat"><span className="stat-label">Tahografi — istekao rok / nikad</span><span className="stat-value text-danger">{bv.istekao}</span></div>
                <div className="stat"><span className="stat-label">Tahografi — ističe za ≤ 15 dana</span><span className="stat-value">{bv.upozorenje + bv.uskoro}</span></div>
            </div>

            <TabView className="page-tabs" activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
                <TabPanel header={`Kartice vozača (${vozaciPrikaz.length})`} leftIcon="pi pi-id-card">
                    <div className="panel">
                        <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (vozač, kartica, firma, datum, stanje...)" label="Pretraga kartica vozača"
                            ukupno={vozaciFilter.length} pronadjeno={vozaciPrikaz.length} jedinica="vozača" />
                        <DataTable value={vozaciPrikaz} dataKey="id" size="small" rowGroupMode="subheader" groupRowsBy="firma" rowGroupHeaderTemplate={grupa}
                            rowClassName={redKlasa} scrollable scrollHeight="62vh" paginator rows={100} rowsPerPageOptions={[50, 100, 250]}
                            emptyMessage={tableEmpty(data ? 'Nema vozača za izabrani prikaz' : 'Učitavanje...', 'pi pi-id-card')}>
                            <Column header="Vozač" field="naziv" style={{ minWidth: '14rem' }} />
                            <Column header="Kartica" style={{ minWidth: '10rem' }} body={(r) => r.cardNumber || <span className="muted">—</span>} />
                            <Column header="Kartica važi do" style={{ minWidth: '9rem' }} body={(r) => (r.cardExpiry
                                ? <span className={r.vaznost ? (r.vaznost.expired ? 'text-danger' : 'text-warn') : ''} title={r.vaznost ? (r.vaznost.expired ? 'Kartica je istekla' : `Kartica ističe za ${r.vaznost.daysLeft} dana`) : undefined}>{fmtDate(r.cardExpiry)}</span>
                                : <span className="muted">—</span>)} />
                            <Column header="Poslednje očitavanje" style={{ minWidth: '10rem' }} body={poslednjeBody} />
                            <Column header="Dana od očitavanja" headerClassName="num" bodyClassName="num" body={(r) => (r.st.daysSince ?? '—')} />
                            <Column header="Stanje" style={{ minWidth: '12rem' }} body={stanjeBody} />
                        </DataTable>
                        <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />
                            * Za podatke uvezene pre uvođenja evidencije očitavanja, poslednje očitavanje je procenjeno prema poslednjem danu sa aktivnostima.</p>
                    </div>
                </TabPanel>
                <TabPanel header={`Tahografi vozila (${vozilaPrikaz.length})`} leftIcon="pi pi-truck">
                    <div className="panel">
                        <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (registracija, šasija, firma, datum, stanje...)" label="Pretraga tahografa vozila"
                            ukupno={vozilaFilter.length} pronadjeno={vozilaPrikaz.length} jedinica="vozila" />
                        <DataTable value={vozilaPrikaz} dataKey="id" size="small" rowGroupMode="subheader" groupRowsBy="firma" rowGroupHeaderTemplate={grupa}
                            rowClassName={redKlasa} scrollable scrollHeight="62vh"
                            emptyMessage={tableEmpty(data ? 'Nema vozila za izabrani prikaz' : 'Učitavanje...', 'pi pi-truck')}>
                            <Column header="Registracija" field="naziv" style={{ minWidth: '10rem' }} />
                            <Column header="Broj šasije" style={{ minWidth: '12rem' }} body={(r) => r.identificationNumber || '—'} />
                            <Column header="Poslednje očitavanje" style={{ minWidth: '10rem' }} body={poslednjeBody} />
                            <Column header="Dana od očitavanja" headerClassName="num" bodyClassName="num" body={(r) => (r.st.daysSince ?? '—')} />
                            <Column header="Stanje" style={{ minWidth: '12rem' }} body={stanjeBody} />
                        </DataTable>
                        <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />
                            Očitavanje tahografa se beleži pri uvozu .ddd fajla vozila — dugmetom Učitaj .DDD fajlove ovde, na Unosu aktivnosti ili u Unosu aktivnosti za vozila.</p>
                    </div>
                </TabPanel>
            </TabView>
            {uvoz && <ImportResultDialog files={uvoz} url="/tacho" onClose={() => { setUvoz(null); ucitaj(); }} />}
        </BlockUI>
    );
}
